import { Button, Col, Form, Image, Input, Modal, Row } from "antd";
import React, { useEffect, useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import notfound from "../../assets/images/not_found.png";
import SingleImageUpload from "../../components/SingleImageUpload";
import apiPath from "../../constants/apiPath";
import { Severty, ShowToast } from "../../helper/toast";
import useRequest from "../../hooks/useRequest";
import { EditOutlined } from "@ant-design/icons";

const ViewAndEditProfile = ({ show, hide, data: userData, refresh }) => {
  const { request } = useRequest();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [profileImage, setProfileImage] = useState("");
  const [isEdit, setIsEdit] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [phoneInputValue, setPhoneInputValue] = useState("91");
  const [phoneDialCode, setPhoneDialCode] = useState("91");

  const user = JSON.parse(localStorage.getItem("userProfile") || "{}");
  const loginData = JSON.parse(localStorage.getItem("loginData") || "{}");
  const authRole = localStorage.getItem("authRole") || "admin";

  const getResolvedContact = (profile) =>
    profile?.contact_no ?? profile?.hotel_phone ?? profile?.phone_no ?? "";

  const getResolvedCountryCode = (profile) =>
    profile?.contact_country_code ?? profile?.country_code ?? profile?.hotel_country_code ?? "+91";

  const normalizeContactNumber = (value) => String(value ?? "").replace(/\D/g, "");

  const parseContactFields = (profile) => {
    const rawContact = String(getResolvedContact(profile) ?? "").trim();
    const parsed = rawContact.match(/^(\+\d{1,4})\s*(\d+)$/);
    if (parsed) {
      return { dialCode: parsed[1].replace("+", ""), numberOnly: parsed[2] };
    }
    const rawCode = getResolvedCountryCode(profile).replace("+", "");
    return { dialCode: rawCode, numberOnly: normalizeContactNumber(rawContact) };
  };

  const getFormattedContact = (profile) => {
    const { dialCode, numberOnly } = parseContactFields(profile);
    if (!numberOnly) return "N/A";
    return `+${dialCode} ${numberOnly}`;
  };

  const getResolvedName = (profile) =>
    profile?.name ?? profile?.Hotel_name ?? profile?.hotel_name ?? "";

  const profileId =
    authRole === "hotel"
      ? (
        user?.Hotel_Id ||
        loginData?.managerDetails?.Hotel_Id ||
        userData?.Hotel_Id ||
        user?.manager_id ||
        userData?.manager_id
      )
      : authRole === "agent"
        ? (user?.agent_id || userData?.agent_id || user?._id || user?.id)
        : (user?._id || user?.id || userData?._id || userData?.id || user?.bus_company_id || userData?.bus_company_id || user?.airline_id || userData?.airline_id || user?.user_id || userData?.user_id || loginData?.busCompanyDetails?._id || loginData?.busCompanyDetails?.id);

  const imageBaseURL = apiPath.assetURL;

  const initPhoneState = (profile) => {
    const { dialCode, numberOnly } = parseContactFields(profile);
    setPhoneDialCode(dialCode);
    setPhoneInputValue(dialCode + numberOnly);
  };

  useEffect(() => {
    if (!show) return;
    const initialProfile = userData || user || {};
    form.setFieldsValue({ name: getResolvedName(initialProfile) });
    initPhoneState(initialProfile);

    if (profileId) {
      fetchUserDetails(profileId);
    }
  }, [show, profileId]);

  const fetchUserDetails = (userId) => {
    if (authRole === "agent") {
      const profile = user || userData || null;
      setUserDetails(profile);
      if (profile) initPhoneState(profile);
      setLoading(false);
      return;
    }
    setLoading(true);
    const profileUrl =
      authRole === "hotel"
        ? `${apiPath.baseURL}/get_HotelPartner/${userId}`
        : `${apiPath.baseURL}${apiPath.getSuperAdmin}/${userId}`;

    request({
      url: profileUrl,
      method: "GET",
      onSuccess: (res) => {
        setLoading(false);
        const resolvedUser =
          res?.HotelPartner_Details ||
          res?.details?.[0] ||
          res?.data?.details?.[0] ||
          res?.data ||
          res?.user ||
          res;
        setUserDetails(resolvedUser);
        form.setFieldsValue({ name: getResolvedName(resolvedUser) });
        initPhoneState(resolvedUser);

        if (resolvedUser?.profileImage) {
          const imageUrl = resolvedUser.profileImage.startsWith("http")
            ? resolvedUser.profileImage
            : `${imageBaseURL}${resolvedUser.profileImage.replace(/^\//, "")}`;
          setProfileImage(imageUrl);
        } else {
          setProfileImage("");
        }

        setIsEdit(false);
      },
      onError: (error) => {
        setLoading(false);
        ShowToast(
          error?.response?.data?.message || "Failed to load profile data",
          Severty.ERROR
        );
      },
    });
  };

  const onCreate = async (values) => {
    if (!profileId) {
      ShowToast("Unable to identify profile", Severty.ERROR);
      return;
    }
    setLoading(true);

    const numberOnly = phoneInputValue.startsWith(phoneDialCode)
      ? phoneInputValue.slice(phoneDialCode.length)
      : phoneInputValue;

    const payload = {
      name: values.name,
      contact_no: `+${phoneDialCode} ${numberOnly}`,
      country_code: `+${phoneDialCode}`,
    };

    if (profileImage) {
      payload.profileImage = profileImage;
    }

    submitForm(payload);
  };

  const submitForm = (payload) => {
    const updateUrl =
      authRole === "hotel"
        ? `${apiPath.baseURL}/update_HotelPartner/${profileId}`
        : authRole === "agent"
          ? `${apiPath.baseURL}/update_agent_profile/${profileId}`
          : `${apiPath.baseURL}/updateSuperAdmin_details/${profileId}`;

    request({
      url: updateUrl,
      method: "PUT",
      data: payload,
      onSuccess: (data) => {
        setLoading(false);
        if (data.status || data.success) {
          ShowToast(data.message || "Profile updated successfully", Severty.SUCCESS);
          setIsEdit(false);

          if (data.data || data.user) {
            const updatedUser = data.data || data.user;
            const currentUser = JSON.parse(localStorage.getItem("userProfile") || "{}");
            const mergedUser = {
              ...currentUser,
              ...updatedUser,
              contact_no: getResolvedContact(updatedUser) ?? getResolvedContact(currentUser),
              name: getResolvedName(updatedUser) || getResolvedName(currentUser),
            };
            localStorage.setItem("userProfile", JSON.stringify(mergedUser));

            const storedLogin = JSON.parse(localStorage.getItem("loginData") || "{}");
            if (authRole === "hotel") {
              storedLogin.managerDetails = {
                ...(storedLogin.managerDetails || {}),
                ...updatedUser,
                contact_no: getResolvedContact(updatedUser) ?? getResolvedContact(storedLogin.managerDetails),
                name: getResolvedName(updatedUser) || getResolvedName(storedLogin.managerDetails),
              };
              localStorage.setItem("loginData", JSON.stringify(storedLogin));
            } else if (authRole === "admin") {
              storedLogin.adminDetails = { ...(storedLogin.adminDetails || {}), ...updatedUser };
              localStorage.setItem("loginData", JSON.stringify(storedLogin));
            } else if (authRole === "agent") {
              storedLogin.agentDetails = { ...(storedLogin.agentDetails || {}), ...updatedUser };
              localStorage.setItem("loginData", JSON.stringify(storedLogin));
            }
          }

          if (profileId) fetchUserDetails(profileId);
          if (refresh) refresh();
          hide();
        } else {
          ShowToast(data.message || "Failed to update profile", Severty.ERROR);
        }
      },
      onError: (error) => {
        ShowToast(
          error?.response?.data?.message || error?.message || "Failed to update profile",
          Severty.ERROR
        );
        setLoading(false);
      },
    });
  };

  const handleCancel = () => {
    setIsEdit(false);
    if (userDetails) {
      form.setFieldsValue({ name: getResolvedName(userDetails) });
      initPhoneState(userDetails);
      if (userDetails?.profileImage) {
        const imageUrl = userDetails.profileImage.startsWith("http")
          ? userDetails.profileImage
          : `${imageBaseURL}${userDetails.profileImage.replace(/^\//, "")}`;
        setProfileImage(imageUrl);
      } else {
        setProfileImage("");
      }
    }
  };

  const getImageUrl = () => {
    if (profileImage) return profileImage;
    if (userDetails?.profileImage) {
      return userDetails.profileImage.startsWith("http")
        ? userDetails.profileImage
        : `${imageBaseURL}${userDetails.profileImage.replace(/^\//, "")}`;
    }
    if (user?.profileImage) {
      return user.profileImage.startsWith("http")
        ? user.profileImage
        : `${imageBaseURL}${user.profileImage.replace(/^\//, "")}`;
    }
    return notfound;
  };

  return (
    <Modal
      open={show}
      width={600}
      title=""
      footer={null}
      onCancel={() => {
        setIsEdit(false);
        hide();
      }}
      className="tab_modal"
    >
      <h4 className="modal_title_cls">Edit Profile</h4>
      <Form id="edit-profile-form" form={form} onFinish={onCreate} layout="vertical">
        <Row gutter={[16, 16]}>
          <Col span={24} style={{ textAlign: "center", marginBottom: 24 }}>
            <div style={{ marginBottom: 16, position: "relative" }}>
              <Image
                src={getImageUrl()}
                width={120}
                height={120}
                style={{
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "3px solid #1890ff",
                }}
                preview={false}
                fallback={notfound}
              />
              {isEdit && (
                <div
                  style={{
                    position: "absolute",
                    bottom: 0,
                    right: "calc(50% - 60px)",
                    background: "#1890ff",
                    borderRadius: "50%",
                    width: 32,
                    height: 32,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                >
                  <EditOutlined style={{ color: "white", fontSize: 16 }} />
                </div>
              )}
            </div>
            {!isEdit && (
              <Button type="primary" onClick={() => setIsEdit(true)}>
                Edit Profile
              </Button>
            )}
          </Col>

          {isEdit && (
            <>
              <Col span={24}>
                <Form.Item label={<strong>Profile Image</strong>} name="image">
                  <SingleImageUpload
                    value={profileImage}
                    onChange={(url) => {
                      setProfileImage(url);
                      form.setFieldsValue({ image: url });
                    }}
                    imageType="profile"
                    btnName="Upload Profile Image"
                    fileType={["image/jpeg", "image/png", "image/jpg"]}
                    size={5}
                  />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  label={<strong>Name</strong>}
                  name="name"
                  rules={[
                    { required: true, message: "Please enter your name!" },
                    { min: 2, message: "Name must be at least 2 characters" },
                    { max: 100, message: "Name must not exceed 100 characters" },
                  ]}
                >
                  <Input placeholder="Enter your name" size="large" />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item label={<strong>Contact No</strong>} required>
                  <PhoneInput
                    country="in"
                    value={phoneInputValue}
                    onChange={(value, data) => {
                      setPhoneInputValue(value);
                      setPhoneDialCode(data.dialCode);
                    }}
                    inputStyle={{ width: "100%", height: 40, fontSize: 14 }}
                    containerStyle={{ width: "100%" }}
                    enableSearch
                    placeholder="Enter contact number"
                  />
                </Form.Item>
              </Col>

              <Col span={24} style={{ marginTop: 16 }}>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
                  <Button onClick={handleCancel} style={{ minWidth: "100px" }} size="large">
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    style={{ minWidth: "120px" }}
                    size="large"
                  >
                    Save Changes
                  </Button>
                </div>
              </Col>
            </>
          )}

          {!isEdit && (
            <Col span={24}>
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <div style={{ padding: "16px", background: "#f5f5f5", borderRadius: "8px" }}>
                    <div style={{ marginBottom: 8, fontSize: "12px", color: "#8c8c8c", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Full Name
                    </div>
                    <div style={{ fontSize: "16px", fontWeight: 500 }}>
                      {getResolvedName(userDetails) || getResolvedName(user) || "N/A"}
                    </div>
                  </div>
                </Col>
                <Col span={24}>
                  <div style={{ padding: "16px", background: "#f5f5f5", borderRadius: "8px" }}>
                    <div style={{ marginBottom: 8, fontSize: "12px", color: "#8c8c8c", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Email Address
                    </div>
                    <div style={{ fontSize: "16px", fontWeight: 500 }}>
                      {userDetails?.email || user?.email || "N/A"}
                    </div>
                  </div>
                </Col>
                <Col span={24}>
                  <div style={{ padding: "16px", background: "#f5f5f5", borderRadius: "8px" }}>
                    <div style={{ marginBottom: 8, fontSize: "12px", color: "#8c8c8c", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Contact No
                    </div>
                    <div style={{ fontSize: "16px", fontWeight: 500 }}>
                      {getFormattedContact(userDetails || user)}
                    </div>
                  </div>
                </Col>
                {(userDetails?.user_type || user?.user_type) && (
                  <Col span={24}>
                    <div style={{ padding: "16px", background: "#f5f5f5", borderRadius: "8px" }}>
                      <div style={{ marginBottom: 8, fontSize: "12px", color: "#8c8c8c", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        User Role
                      </div>
                      <div style={{ fontSize: "16px", fontWeight: 500 }}>
                        <span style={{ background: "#e6f7ff", padding: "4px 12px", borderRadius: "4px", color: "#1890ff" }}>
                          {userDetails?.user_type || user?.user_type}
                        </span>
                      </div>
                    </div>
                  </Col>
                )}
              </Row>
            </Col>
          )}
        </Row>
      </Form>
    </Modal>
  );
};

export default ViewAndEditProfile;
